import { useMemo, useState, useRef, useEffect } from "react";
import { useAuth } from "../../contexts/auth/Context";
import { decodeUserInfo } from "../../utils/user";
import {
  getMenuCategories,
  getCategoryIcon,
  getSuperAdminMenuItems,
  type SuperAdminMenuItem,
} from "../../utils/navbar-links";
import { canAccessRoute, type UserRole } from "../../utils/route-permissions";
import { useCurrentGym } from "../gym/useCurrentGym";
import { usePendingAppBannersCount } from "../admin/useAppBannerReviews";

export type { SuperAdminMenuItem };

export interface MenuItem {
  label: string;
  href: string;
}

export interface MenuCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: MenuItem[];
}

export const useNavbar = () => {
  const { token, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const { gymName, email, role } = decodeUserInfo(token);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const normalizedRole = (role?.trim().toUpperCase() as UserRole) || null;
  const isSuperAdmin = normalizedRole === "SUPERADMIN";
  const { data: gym } = useCurrentGym();

  const pendingBanners = usePendingAppBannersCount(isSuperAdmin);

  const superAdminMenuItems = useMemo<SuperAdminMenuItem[]>(
    () => (isSuperAdmin ? getSuperAdminMenuItems(pendingBanners) : []),
    [isSuperAdmin, pendingBanners],
  );

  const modulesSettings = gym?.modulesSettings;

  const menuCategories: MenuCategory[] = useMemo(() => {
    if (isSuperAdmin) {
      return [];
    }

    const categories = getMenuCategories(modulesSettings).map((category) => ({
      ...category,
      icon: getCategoryIcon(category.id),
      items: category.items.filter((item) =>
        canAccessRoute(normalizedRole, item.href),
      ),
    }));

    return categories.filter((category) => category.items.length > 0);
  }, [isSuperAdmin, normalizedRole, modulesSettings]);

  useEffect(() => {
    if (!openDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const ref = dropdownRefs.current[openDropdown];

      if (ref && !ref.contains(target)) {
        setOpenDropdown(null);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (mobileMenuRef.current?.contains(target)) return;
      if (target.closest('button[aria-label="Toggle menu"]')) return;

      setIsMenuOpen(false);
      setOpenDropdown(null);
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isMenuOpen]);

  const toggleDropdown = (categoryId: string) => {
    setOpenDropdown(openDropdown === categoryId ? null : categoryId);
  };

  const handleMobileMenuToggle = () => {
    setIsMenuOpen((prev) => {
      if (prev) setOpenDropdown(null);
      return !prev;
    });
  };

  const closeMobileMenu = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  return {
    gymName,
    email,
    role,
    isSuperAdmin,
    normalizedRole,
    logout,
    superAdminMenuItems,
    menuCategories,
    isMenuOpen,
    openDropdown,
    mobileMenuRef,
    dropdownRefs,
    toggleDropdown,
    handleMobileMenuToggle,
    closeMobileMenu,
    setOpenDropdown,
  };
};
